import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';

import { Plans } from '@labradorsports/constants';
import { inIframe, isIOS, useSite } from '@labradorsports/utils';

import { VersionCheck } from './app/index.js';
import initPlugins from './app/plugins/index.js';
import { PaymentPage as SSRPaymentPage } from './billing/index.js';
import { CommunityPage as SSRCommunityPage } from './community/index.js';
import { DashboardPage as SSRDashboardPage, DocumentsPage as SSRDocumentsPage } from './dashboard/index.js';
import fonts from './fonts.js';
import {
    JoinTeamPage,
    EmailActionPage,
    AffiliateLinkPage,
    LoginForm,
    SignupForm,
    ForgotPasswordForm,
    LoginPage,
} from './main/index.js';
import { PlaybookPage as SSRPlaybookPage, PublicSharedPlayPage as SSRPublicSharedPlayPage } from './playbook/index.js';
import { ProfilePage as SSRProfilePage } from './profile/index.js';
import { SchedulePage as SSRSchedulePage } from './schedule/index.js';
import { default as SSRScheduleUpsell } from './schedule/schedule-upsell/schedule-upsell.js';
import { OfflineBanner, NavigateWithQuery, useLogger } from './shared/index.js';
import { PrivateRoute, lazyWrapper, SiteChrome } from './shared/structural/index.js';
import { Selectors, useGetProfileFlagsQuery } from './store/index.js';
import { MyTeam as SSRMyTeam, MyTeamUpsell as SSRMyTeamUpsell } from './team-management/index.js';

declare global {
    interface Window {
        __GOOGLEBOT__: boolean;
    }
}

const {
    PlaybookPage,
    MyTeam,
    MyTeamUpsell,
    ProfilePage,
    SchedulePage,
    ScheduleUpsell,
    PublicSharedPlayPage,
    DashboardPage,
    CommunityPage,
    DocumentsPage,
    PaymentPage,
} = SSR
    ? {
          PlaybookPage: SSRPlaybookPage,
          MyTeam: SSRMyTeam,
          MyTeamUpsell: SSRMyTeamUpsell,
          ProfilePage: SSRProfilePage,
          SchedulePage: SSRSchedulePage,
          ScheduleUpsell: SSRScheduleUpsell,
          PublicSharedPlayPage: SSRPublicSharedPlayPage,
          DashboardPage: SSRDashboardPage,
          CommunityPage: SSRCommunityPage,
          DocumentsPage: SSRDocumentsPage,
          PaymentPage: SSRPaymentPage,
      }
    : {
          PlaybookPage: lazyWrapper(
              () => import(/* webpackChunkName: "playbook" */ './playbook/playbook-page/playbook-page.js')
          ),
          MyTeam: lazyWrapper(() => import(/* webpackChunkName: "my-team" */ './team-management/my-team/my-team.js')),
          MyTeamUpsell: lazyWrapper(
              () =>
                  import(/* webpackChunkName: "my-team-upsell" */ './team-management/my-team-upsell/my-team-upsell.js')
          ),
          ProfilePage: lazyWrapper(
              () => import(/* webpackChunkName: "profile" */ './profile/profile-page/profile-page.js')
          ),
          SchedulePage: lazyWrapper(
              () => import(/* webpackChunkName: "schedule" */ './schedule/schedule-page/schedule-page.js')
          ),
          ScheduleUpsell: lazyWrapper(
              () => import(/* webpackChunkName: "schedule-upsell" */ './schedule/schedule-upsell/schedule-upsell.js')
          ),
          PublicSharedPlayPage: lazyWrapper(
              () =>
                  import(
                      /* webpackChunkName: "publicSharedPlay" */ './playbook/public-shared-play-page/public-shared-play-page.js'
                  )
          ),
          DashboardPage: lazyWrapper(
              () => import(/* webpackChunkName: "dashboard" */ './dashboard/dashboard-page/dashboard-page.js')
          ),
          CommunityPage: lazyWrapper(
              () => import(/* webpackChunkName: "community" */ './community/community-page/community-page.js')
          ),
          DocumentsPage: lazyWrapper(
              () => import(/* webpackChunkName: "documents" */ './dashboard/documents-page/documents-page.js')
          ),
          PaymentPage: lazyWrapper(
              () => import(/* webpackChunkName: "billing" */ './billing/payment-page/payment-page.js')
          ),
      };

const routesWithoutChrome = ['/play', '/playbook/team', '/community/embed'];

const Content: FC = () => {
    const { Config } = useSite();
    const location = useLocation();
    const logger = useLogger();
    const bodyRef = useRef(null);
    const user = useSelector(Selectors.user);

    useGetProfileFlagsQuery({ uid: user?.uid }, { skip: !user });

    useEffect(() => {
        if (APP) {
            initPlugins(logger);
        } else if (!E2E && !window.__GOOGLEBOT__ && !inIframe()) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = 'https://app.termly.io/resource-blocker/84fbec14-511f-4332-a88f-a1b8242a6326';
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    useEffect(() => {
        if (bodyRef.current) {
            // cannot use Element.scrollTo() because it is not supported in Edge
            bodyRef.current.scrollTop = 0;
        }
    }, [location]);

    const classNames = ['body'];

    if (user) {
        classNames.push('logged-in');
    }

    if (isIOS()) {
        classNames.push('ios');
    }

    const noChrome = routesWithoutChrome.includes(location.pathname) && inIframe();

    return (
        <SiteChrome hideChrome={noChrome}>
            {APP ? (
                <VersionCheck />
            ) : (
                <Helmet>
                    <style>{fonts}</style>
                </Helmet>
            )}
            <div className={classNames.join(' ')} ref={bodyRef}>
                <Routes>
                    <Route path="/login" element={<LoginPage FormComponent={LoginForm} />} />
                    <Route path="/signup" element={<LoginPage FormComponent={SignupForm} />} />
                    <Route path="/forgot-password" element={<LoginPage FormComponent={ForgotPasswordForm} />} />
                    <Route path="/email-action" element={<EmailActionPage />} />
                    <Route path="/play" element={<PublicSharedPlayPage />} />
                    <Route
                        path="/feed/*"
                        element={
                            <PrivateRoute>
                                <DashboardPage />
                            </PrivateRoute>
                        }
                    />
                    {Config.Features.PlayDesigner ? (
                        <Route
                            path="/playbook/*"
                            element={
                                <PrivateRoute>
                                    <PlaybookPage />
                                </PrivateRoute>
                            }
                        />
                    ) : null}
                    {Config.Features.Schedule && Config.Features.TeamAdmin ? (
                        <Route
                            path="/schedule/*"
                            element={
                                <PrivateRoute
                                    fallback={<ScheduleUpsell />}
                                    requireSubscription
                                    excludePlans={[Plans.BASIC]}
                                >
                                    <SchedulePage />
                                </PrivateRoute>
                            }
                        />
                    ) : null}
                    <Route
                        path="/profile/*"
                        element={
                            <PrivateRoute>
                                <ProfilePage />
                            </PrivateRoute>
                        }
                    />
                    {Config.Features.Roster && Config.Features.TeamAdmin ? (
                        <Route
                            path="/team/*"
                            element={
                                <PrivateRoute fallback={<MyTeamUpsell />} requireSubscription>
                                    <MyTeam />
                                </PrivateRoute>
                            }
                        />
                    ) : null}
                    {Config.Features.Community ? <Route path="/community/*" element={<CommunityPage />} /> : null}
                    <Route
                        path="/join-team"
                        element={
                            <PrivateRoute>
                                <JoinTeamPage />
                            </PrivateRoute>
                        }
                    />
                    {Config.Features.TeamDocuments ? (
                        <Route
                            path="/documents/*"
                            element={
                                <PrivateRoute requireSubscription excludePlans={[Plans.BASIC]}>
                                    <DocumentsPage />
                                </PrivateRoute>
                            }
                        />
                    ) : null}
                    {APP ? null : <Route path="/payment/*" element={<PaymentPage />} />}
                    {APP ? null : <Route path="/:code" element={<AffiliateLinkPage />} />}
                    <Route path="*" element={<NavigateWithQuery to="/feed" />} />
                </Routes>
                <OfflineBanner />
            </div>
        </SiteChrome>
    );
};

export default Content;
